import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { fetchReportGeometry } from '../services/mapFeedbackService';
import './MapFeedbackGeometry.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const STYLE = 'mapbox://styles/mapbox/dark-v11';

const COLOR_BY_TYPE = { run: '#3b82f6', lift: '#fb923c', freeform: '#a855f7' };

/** Bounds of any GeoJSON geometry, flat enough for what the resort DB stores. */
const boundsOf = (geometry) => {
  const bounds = new mapboxgl.LngLatBounds();
  const walk = (coords) => {
    if (typeof coords[0] === 'number') bounds.extend(coords);
    else coords.forEach(walk);
  };
  if (!geometry?.coordinates) return null;
  walk(geometry.coordinates);
  return bounds.isEmpty() ? null : bounds;
};

/**
 * The geometry behind one report — the flagged run or lift, or a pin at the
 * coordinate of a freeform report.
 *
 * Mounted only when the operator expands a card, so the list itself never pays
 * for map geometry it is not showing.
 */
const MapFeedbackGeometry = ({ report }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [feature, setFeature] = useState(null);
  const [error, setError] = useState(null);

  const request = useMemo(() => ({
    type: report.type,
    skiAreaId: report.skiAreaId,
    featureId: report.featureId,
    latitude: report.latitude,
    longitude: report.longitude,
  }), [report.type, report.skiAreaId, report.featureId, report.latitude, report.longitude]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setFeature(null);
    fetchReportGeometry(request)
      .then(({ feature: f }) => { if (!cancelled) setFeature(f); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [request]);

  useEffect(() => {
    if (!feature || !containerRef.current || mapRef.current) return;
    if (!MAPBOX_TOKEN) { setError('VITE_MAPBOX_TOKEN is not set.'); return; }

    const bounds = boundsOf(feature.geometry);
    const color = COLOR_BY_TYPE[report.type] || '#38bdf8';

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLE,
      center: bounds ? bounds.getCenter() : [0, 0],
      zoom: 13,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current = map;

    map.on('load', () => {
      map.addSource('mfg-feature', { type: 'geojson', data: feature });

      if (feature.geometry.type === 'Point') {
        map.addLayer({
          id: 'mfg-point',
          type: 'circle',
          source: 'mfg-feature',
          paint: {
            'circle-radius': 8,
            'circle-color': color,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#0f172a',
          },
        });
      } else {
        map.addLayer({
          id: 'mfg-line',
          type: 'line',
          source: 'mfg-feature',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-width': 4, 'line-color': color },
        });
      }

      // A single line has zero area in one dimension, so fitBounds needs the
      // padding to avoid zooming past maxZoom on a perfectly straight lift.
      if (bounds) map.fitBounds(bounds, { padding: 48, maxZoom: 15, duration: 0 });
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [feature, report.type]);

  if (error) return <div className="mfg-error">Map unavailable: {error}</div>;
  if (!feature) return <div className="mfg-loading">Loading map…</div>;
  return <div className="mfg-map" ref={containerRef} />;
};

export default MapFeedbackGeometry;
